import { useEffect, useRef, useState, type FocusEvent } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { ITEM_CATEGORY_SUGGESTIONS, normalizeItemCategory } from "@egalregal/shared";
import { useT } from "../i18n";
import { itemCategoryLabel } from "./CategoryChip";

/**
 * Free-solo item-category picker (UI label: Type). The suggestion list stays
 * closed while storage is open: the user can pick Book/Food/Device/Media/Other
 * or type a custom value.
 *
 * The input text is controlled separately from the committed value so typing is
 * never rewritten mid-keystroke (including the space in "cd rom"). Typed text
 * that matches a suggestion's stored value or translated label commits the
 * stored value; anything else commits the trimmed, lowercased custom text.
 * Selection and blur restore the display label for the committed value.
 */
export function CategoryAutocomplete({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (category: string) => void;
  label: string;
}) {
  const t = useT();
  const options = [...ITEM_CATEGORY_SUGGESTIONS];
  const [inputValue, setInputValue] = useState(() => itemCategoryLabel(value, t));
  // The last value this picker committed, so an outside value change can be
  // told apart from the echo of the user's own keystroke.
  const lastCommitted = useRef(value);

  useEffect(() => {
    if (value === lastCommitted.current) return;
    lastCommitted.current = value;
    setInputValue(itemCategoryLabel(value, t));
  }, [value, t]);

  /** Match a translated or stored suggestion, case-insensitively. */
  const matchSuggestion = (text: string): string | null => {
    const query = normalizeItemCategory(text);
    if (!query) return null;
    return (
      ITEM_CATEGORY_SUGGESTIONS.find(
        (option) =>
          option === query || normalizeItemCategory(itemCategoryLabel(option, t)) === query,
      ) ?? null
    );
  };

  /** Store a suggestion's value or the normalized custom text. */
  const commit = (text: string): string => {
    const stored = matchSuggestion(text) ?? normalizeItemCategory(text);
    lastCommitted.current = stored;
    onChange(stored);
    return stored;
  };

  const handleInputChange = (_event: unknown, next: string, reason: string) => {
    // MUI resyncs the input from the committed value; the typed text wins.
    if (reason === "reset") return;
    setInputValue(next);
    commit(next);
  };

  const handleChange = (_event: unknown, next: string | null) => {
    if (next === null) {
      lastCommitted.current = "";
      onChange("");
      setInputValue("");
      return;
    }
    const stored = commit(next);
    setInputValue(itemCategoryLabel(stored, t));
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      size="small"
      options={options}
      value={value}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => itemCategoryLabel(option, t)}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            {itemCategoryLabel(option, t)}
          </li>
        );
      }}
      filterOptions={(candidates, state) => {
        const query = normalizeItemCategory(state.inputValue);
        if (!query) return candidates;
        return candidates.filter(
          (option) =>
            option.includes(query) ||
            itemCategoryLabel(option, t).toLowerCase().includes(query),
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={{
            ...params.inputProps,
            onBlur: (event) => {
              params.inputProps.onBlur?.(event as FocusEvent<HTMLInputElement>);
              setInputValue(itemCategoryLabel(lastCommitted.current, t));
            },
          }}
        />
      )}
    />
  );
}
